import React from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { IconChevronDown } from '@shared/assets/images/svgs';
import CustomFormLabel from '@shared/components/forms/theme-elements/CustomFormLabel';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import { CallDto } from '@shared/services/apiService/apiService';
import { useTranslation } from 'react-i18next';

import { formatCampaignWebhookCallLabel } from '@features/Admin/Campaign/utils/campaign-webhook-ui';

interface CampaignWebhookDispatchDialogProps {
  open: boolean;
  endedCalls: CallDto[];
  selectedCallId: string;
  disabled: boolean;
  isLoading: boolean;
  isCallsLoading: boolean;
  onChangeCall: (callId: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}

const CampaignWebhookDispatchDialog: React.FC<CampaignWebhookDispatchDialogProps> = ({
  open,
  endedCalls,
  selectedCallId,
  disabled,
  isLoading,
  isCallsLoading,
  onChangeCall,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('campaignPage.webhooks.testDispatchTitle')}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">{t('campaignPage.webhooks.testDispatchDialogHint')}</Alert>
          <div>
            <CustomFormLabel htmlFor="test-call" sx={{ mb: 1, fontWeight: 500 }}>
              {t('campaignPage.webhooks.selectCall')}
            </CustomFormLabel>
            <CustomSelect
              id="test-call"
              fullWidth
              value={selectedCallId}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
                onChangeCall(event.target.value as string)
              }
              disabled={disabled || isCallsLoading}
              IconComponent={IconChevronDown}>
              {endedCalls.map((call) => (
                <MenuItem key={call.id} value={call.id}>
                  {formatCampaignWebhookCallLabel(call)}
                </MenuItem>
              ))}
            </CustomSelect>
          </div>
          {!isCallsLoading && endedCalls.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('campaignPage.webhooks.testDispatchDisabledNoCalls')}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={disabled || !selectedCallId}>
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t('campaignPage.webhooks.testDispatch')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignWebhookDispatchDialog;
